import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { fetchCategories, deleteCategory } from '../../actions/categoryActions';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import CategoryForm from '../../components/admin/CategoryForm';

const CategoriesPage = () => {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  const dispatch = useDispatch();
  
  const categoryList = useSelector((state) => state.categoryList);
  const { loading, error, categories } = categoryList;
  
  const categoryDelete = useSelector((state) => state.categoryDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = categoryDelete;
  
  const categoryCreate = useSelector((state) => state.categoryCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
  } = categoryCreate;
  
  const categoryUpdate = useSelector((state) => state.categoryUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = categoryUpdate;
  
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch, successDelete, successCreate, successUpdate]);
  
  const openCreateModal = () => {
    setCurrentCategory(null);
    setShowCategoryModal(true);
  };
  
  const openEditModal = (category) => {
    setCurrentCategory(category);
    setShowCategoryModal(true);
  };
  
  const closeModal = () => {
    setShowCategoryModal(false);
    setCurrentCategory(null);
  };
  
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = () => {
    dispatch(deleteCategory(categoryToDelete.id));
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };
  
  return (
    <div className="admin-categories-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">分类管理</h1>
        <button
          onClick={openCreateModal}
          className="bg-green-600 text-white px-4 py-2 rounded flex items-center hover:bg-green-700"
        >
          <FaPlus className="mr-2" />
          添加分类
        </button>
      </div>
      
      {(loadingDelete || loadingCreate || loadingUpdate) && <Loader />}
      {errorDelete && <Message variant="error">{errorDelete}</Message>}
      {errorCreate && <Message variant="error">{errorCreate}</Message>}
      {errorUpdate && <Message variant="error">{errorUpdate}</Message>}
      {successDelete && <Message variant="success">分类已成功删除</Message>}
      {successCreate && <Message variant="success">分类已成功创建</Message>}
      {successUpdate && <Message variant="success">分类已成功更新</Message>}
      
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="error">{error}</Message>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  名称
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  描述
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  关联产品数
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {category.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.product_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(category)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(category)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Category Form Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {currentCategory ? '编辑分类' : '添加分类'}
            </h2>
            <CategoryForm category={currentCategory} onClose={closeModal} />
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-4">确认删除</h3>
            <p className="mb-6">
              您确定要删除分类 "{categoryToDelete.name}" 吗？
              {categoryToDelete.product_count > 0 && (
                <span className="text-red-600 block mt-2">
                  警告：此分类下有 {categoryToDelete.product_count} 个产品。删除此分类可能会影响这些产品。
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                {loadingDelete ? <Loader size="sm" /> : '删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;